import * as THREE from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';

import type { FrameWorkQueue } from './frame-work-queue.js';

const CELL_SIZE = 1024;
const MAX_BATCH_UNITS = 8192;

export interface LineStyle {
  width: number;
  resolution: THREE.Vector2;
}

type PrimitiveKind = 'point' | 'line' | 'polygon';

interface Member {
  guid: string;
  positions: Float32Array;
  indices: number[] | Uint32Array;
  visible: boolean;
  chunk: GeometryChunk;
}

/** Ranges refer to point indices, line instances, or polygon index-buffer entries. */
export interface FeatureRange {
  guid: string;
  start: number;
  count: number;
}

/**
 * A tile/layer's render geometry, partitioned into bounded spatial batches.
 * Feature data remains independent for draping, proximity queries and hide/show.
 * Dirty batches upload once after draping, not once per changed feature.
 */
export class OverlayGeometryBatch {
  readonly group = new THREE.Group();
  private readonly tails = new Map<string, GeometryChunk>();
  private readonly chunks: GeometryChunk[] = [];
  private readonly members = new Map<string, Member[]>();
  private readonly dirty = new Set<GeometryChunk>();
  private readonly pendingUploads = new Set<GeometryChunk>();
  private readonly uploads = new AbortController();
  private camera?: THREE.Camera;
  private pointMaterial?: THREE.PointsMaterial;
  private polygonMaterial?: THREE.MeshBasicMaterial;
  private lineMaterial?: LineMaterial;
  private lineWidth: number;
  private readonly resolution: THREE.Vector2;

  constructor(
    lineStyle: LineStyle,
    private readonly color: THREE.ColorRepresentation,
    private readonly workQueue?: FrameWorkQueue,
  ) {
    this.lineWidth = lineStyle.width;
    this.resolution = lineStyle.resolution.clone();
  }

  add(
    guid: string,
    kind: PrimitiveKind,
    positions: Float32Array,
    indices: number[] | Uint32Array = [],
    center?: [number, number],
  ): () => void {
    const units = kind === 'line' ? positions.length / 3 - 1 : positions.length / 3;
    if (!center) {
      const bounds = new THREE.Box3().setFromArray(positions);
      center = [(bounds.min.x + bounds.max.x) / 2, (bounds.min.y + bounds.max.y) / 2];
    }
    const key = `${kind}/${Math.floor(center[0] / CELL_SIZE)}/${Math.floor(center[1] / CELL_SIZE)}`;
    let chunk = this.tails.get(key);
    if (!chunk || chunk.units + units > MAX_BATCH_UNITS) {
      chunk = new GeometryChunk(kind, this.createObject(kind));
      this.tails.set(key, chunk);
      this.chunks.push(chunk);
      this.group.add(chunk.object);
    }

    const member: Member = { guid, positions, indices, visible: true, chunk };
    chunk.add(member, units);
    const members = this.members.get(guid);
    if (members) members.push(member);
    else this.members.set(guid, [member]);
    this.dirty.add(chunk);
    return () => this.dirty.add(member.chunk);
  }

  setFeatureVisible(guid: string, visible: boolean): void {
    for (const member of this.members.get(guid) ?? []) {
      if (member.visible === visible) continue;
      member.visible = visible;
      member.chunk.topologyDirty = true;
      this.dirty.add(member.chunk);
    }
  }

  setLineWidth(width: number): void {
    this.lineWidth = width;
    if (this.lineMaterial) this.lineMaterial.linewidth = width;
  }

  setResolution(resolution: THREE.Vector2): void {
    this.resolution.copy(resolution);
    this.lineMaterial?.resolution.copy(resolution);
  }

  flush(camera?: THREE.Camera): void {
    this.camera = camera;
    for (const chunk of this.dirty) {
      if (this.pendingUploads.has(chunk)) continue;
      if (!this.workQueue) {
        chunk.flush();
        this.dirty.delete(chunk);
        continue;
      }
      this.pendingUploads.add(chunk);
      const upload = function* (batch: OverlayGeometryBatch): Generator<void> {
        chunk.flush();
        batch.dirty.delete(chunk);
        if (batch.camera) {
          batch.group.updateWorldMatrix(true, false);
          chunk.padScreenSpaceBounds(batch.camera, batch.resolution, batch.lineWidth);
        }
        yield;
      };
      void this.workQueue
        .run(upload(this), this.uploads.signal, () => this.group.parent?.visible !== false)
        .then(
          () => this.pendingUploads.delete(chunk),
          error => {
            this.pendingUploads.delete(chunk);
            if (!(error instanceof Error && error.name === 'AbortError')) {
              console.error('Failed to upload overlay geometry:', error);
            }
          },
        );
    }
    if (camera) {
      camera.updateWorldMatrix(true, false);
      this.group.updateWorldMatrix(true, false);
      for (const chunk of this.chunks) {
        chunk.padScreenSpaceBounds(camera, this.resolution, this.lineWidth);
      }
    }
  }

  dispose(): void {
    this.uploads.abort();
    this.pendingUploads.clear();
    for (const chunk of this.chunks) chunk.object.geometry.dispose();
    this.pointMaterial?.dispose();
    this.polygonMaterial?.dispose();
    this.lineMaterial?.dispose();
    this.group.clear();
    this.chunks.length = 0;
    this.tails.clear();
    this.members.clear();
    this.dirty.clear();
  }

  private createObject(kind: PrimitiveKind): THREE.Mesh | THREE.Points {
    let object: THREE.Mesh | THREE.Points;
    if (kind === 'line') {
      this.lineMaterial ??= new LineMaterial({
        color: this.color,
        linewidth: this.lineWidth,
        transparent: true,
        opacity: 0.9,
        depthWrite: false,
        resolution: this.resolution,
      });
      object = new LineSegments2(new LineSegmentsGeometry(), this.lineMaterial);
    } else if (kind === 'polygon') {
      this.polygonMaterial ??= new THREE.MeshBasicMaterial({
        color: this.color,
        transparent: true,
        opacity: 0.5,
        side: THREE.DoubleSide,
        depthWrite: false,
      });
      object = new THREE.Mesh(new THREE.BufferGeometry(), this.polygonMaterial);
    } else {
      if (!this.pointMaterial) {
        this.pointMaterial = new THREE.PointsMaterial({
          color: this.color,
          size: 14,
          sizeAttenuation: false,
          transparent: true,
          depthTest: false,
          depthWrite: false,
        });
        this.pointMaterial.onBeforeCompile = shader => {
          shader.fragmentShader = shader.fragmentShader.replace(
            '#include <color_fragment>',
            `#include <color_fragment>
            float radius = length(gl_PointCoord - vec2(0.5));
            float edgeWidth = fwidth(radius);
            if (radius >= 0.5) discard;
            diffuseColor.a *= 1.0 - smoothstep(0.5 - edgeWidth, 0.5, radius);`,
          );
        };
      }
      object = new THREE.Points(new THREE.BufferGeometry(), this.pointMaterial);
    }
    object.renderOrder = kind === 'point' ? 1001 : 1000;
    object.frustumCulled = true;
    object.visible = false;
    return object;
  }
}

class GeometryChunk {
  units = 0;
  topologyDirty = true;
  private indexCount = 0;
  private readonly members: Member[] = [];
  private positions = new Float32Array(0);
  private indices = new Uint32Array(0);
  private positionAttribute?: THREE.BufferAttribute;
  private indexAttribute?: THREE.BufferAttribute;
  private segmentBuffer?: THREE.InstancedInterleavedBuffer;
  private readonly bounds = new THREE.Box3();
  private readonly point = new THREE.Vector3();
  private baseRadius = 0;

  constructor(
    private readonly kind: PrimitiveKind,
    readonly object: THREE.Mesh | THREE.Points,
  ) {}

  add(member: Member, units: number): void {
    this.members.push(member);
    this.units += units;
    this.indexCount += member.indices.length;
    this.topologyDirty = true;
  }

  flush(): void {
    this.ensureCapacity();
    const ranges: FeatureRange[] = [];
    let vertexCount = 0;
    let indexCount = 0;
    for (const member of this.members) {
      if (!member.visible) continue;
      const start =
        this.kind === 'polygon' ? indexCount : vertexCount / (this.kind === 'line' ? 2 : 1);
      if (this.kind === 'line') {
        // Explicit pairs keep independent lines disconnected within one instanced draw.
        for (let i = 0; i < member.positions.length - 3; i += 3) {
          this.positions.set(member.positions.subarray(i, i + 6), vertexCount * 3);
          vertexCount += 2;
        }
      } else {
        this.positions.set(member.positions, vertexCount * 3);
        if (this.topologyDirty) {
          for (const index of member.indices) this.indices[indexCount++] = vertexCount + index;
        } else {
          indexCount += member.indices.length;
        }
        vertexCount += member.positions.length / 3;
      }
      const end =
        this.kind === 'polygon' ? indexCount : vertexCount / (this.kind === 'line' ? 2 : 1);
      if (this.topologyDirty) ranges.push({ guid: member.guid, start, count: end - start });
    }

    const geometry = this.object.geometry;
    if (geometry instanceof LineSegmentsGeometry) {
      geometry.instanceCount = vertexCount / 2;
    } else {
      geometry.setDrawRange(0, this.kind === 'polygon' ? indexCount : vertexCount);
    }
    if (this.topologyDirty) this.object.userData['featureRanges'] = ranges;
    this.object.visible = vertexCount > 0;

    // Capacity can exceed the live data. Never let unused buffer entries (zeros)
    // enlarge the bounds back to the origin or retain a hidden feature's bounds.
    this.bounds.makeEmpty();
    for (let i = 0; i < vertexCount * 3; i += 3) {
      this.bounds.expandByPoint(this.point.fromArray(this.positions, i));
    }
    geometry.boundingBox = this.bounds.clone();
    geometry.boundingSphere = this.bounds.getBoundingSphere(new THREE.Sphere());
    this.baseRadius = geometry.boundingSphere.radius;

    updateRange(this.positionAttribute ?? this.segmentBuffer, vertexCount * 3);
    if (this.topologyDirty) updateRange(this.indexAttribute, indexCount);
    this.topologyDirty = false;
  }

  padScreenSpaceBounds(camera: THREE.Camera, resolution: THREE.Vector2, lineWidth: number): void {
    if (this.kind === 'polygon' || !this.object.visible) return;
    const sphere = this.object.geometry.boundingSphere;
    if (!sphere) return;
    this.object.updateWorldMatrix(false, false);
    const scale = this.object.matrixWorld.getMaxScaleOnAxis();
    if (scale === 0) return;
    this.point
      .copy(sphere.center)
      .applyMatrix4(this.object.matrixWorld)
      .applyMatrix4(camera.matrixWorldInverse);
    const depth =
      camera instanceof THREE.PerspectiveCamera
        ? Math.max(0, -this.point.z + this.baseRadius * scale)
        : 1;
    const projection = camera.projectionMatrix.elements;
    const metresPerPixel = Math.max(
      (2 * depth) / (Math.abs(projection[0]) * Math.max(1, resolution.x)),
      (2 * depth) / (Math.abs(projection[5]) * Math.max(1, resolution.y)),
    );
    // Screen-wide strokes can still cover pixels with their centreline outside
    // the frustum. Pad their bounds at the current zoom to avoid edge popping.
    const pixelRadius = this.kind === 'point' ? 7 : lineWidth / 2;
    sphere.radius = this.baseRadius + (Math.SQRT2 * pixelRadius * metresPerPixel) / scale;
  }

  private ensureCapacity(): void {
    const floats = this.units * (this.kind === 'line' ? 6 : 3);
    if (this.positions.length >= floats && this.indices.length >= this.indexCount) return;

    // Dispose the old GPU buffers before replacing attributes on growth.
    this.object.geometry.dispose();
    const stride = this.kind === 'line' ? 6 : 3;
    this.positions = new Float32Array(Math.ceil(nextCapacity(floats) / stride) * stride);
    if (this.kind === 'line') {
      const geometry = new LineSegmentsGeometry();
      this.segmentBuffer = new THREE.InstancedInterleavedBuffer(this.positions, 6);
      this.segmentBuffer.setUsage(THREE.DynamicDrawUsage);
      geometry.setAttribute(
        'instanceStart',
        new THREE.InterleavedBufferAttribute(this.segmentBuffer, 3, 0),
      );
      geometry.setAttribute(
        'instanceEnd',
        new THREE.InterleavedBufferAttribute(this.segmentBuffer, 3, 3),
      );
      this.object.geometry = geometry;
    } else {
      const geometry = new THREE.BufferGeometry();
      this.positionAttribute = new THREE.BufferAttribute(this.positions, 3).setUsage(
        THREE.DynamicDrawUsage,
      );
      geometry.setAttribute('position', this.positionAttribute);
      if (this.kind === 'polygon') {
        this.indices = new Uint32Array(nextCapacity(this.indexCount));
        this.indexAttribute = new THREE.BufferAttribute(this.indices, 1).setUsage(
          THREE.DynamicDrawUsage,
        );
        geometry.setIndex(this.indexAttribute);
      }
      this.object.geometry = geometry;
    }
  }
}

function nextCapacity(required: number): number {
  return 2 ** Math.ceil(Math.log2(Math.max(64, required)));
}

function updateRange(
  attribute: THREE.BufferAttribute | THREE.InterleavedBuffer | undefined,
  count: number,
): void {
  if (!attribute || count === 0) return;
  attribute.clearUpdateRanges();
  attribute.addUpdateRange(0, count);
  attribute.needsUpdate = true;
}
